// Calculator funnel analytics. One place so every investor tool emits the same
// event names and dimensions: input changes -> estimate viewed -> CTA clicks ->
// lead submission. All helpers are safe no-ops on the server.

import { trackEvent, trackEventThrottled } from "@/lib/analytics";

export type CalculatorKey =
  | "arv"
  | "cash_on_cash"
  | "cap_rate"
  | "brrrr"
  | "dscr"
  | "rent_needed"
  | "dscr_payment"
  | "ltv_equity"
  | "prepayment_penalty"
  | "io_vs_amortizing"
  | "closing_cost"
  | "cash_out_refi"
  | "deal_analyzer";

type Params = Record<string, string | number | boolean | null | undefined>;

const base = (calculator: CalculatorKey, params: Params = {}): Params => ({
  calculator,
  ...params,
});

/** A user edited an input. Throttled so dragging/typing doesn't flood GA4. */
export function trackCalcInput(
  calculator: CalculatorKey,
  field: string,
  value: number | string,
): void {
  trackEventThrottled("calculator_input_changed", base(calculator, { field, value }), 1200);
}

/** The result panel became visible with a usable estimate. Fire once per session view. */
export function trackCalcEstimateViewed(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_estimate_viewed", base(calculator, params));
}

/** Any conversion CTA inside a calculator (apply, rates, related tool, share). */
export function trackCalcCta(
  calculator: CalculatorKey,
  cta: string,
  params: Params = {},
): void {
  trackEvent("calculator_cta_click", base(calculator, { cta, ...params }));
}

/** Lead gate impression. */
export function trackCalcLeadGateViewed(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_lead_gate_viewed", base(calculator, params));
}

/** Lead form submit attempt (before the server responds). */
export function trackCalcLeadFormSubmit(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_lead_form_submitted", base(calculator, params));
}

/** Lead gate submitted successfully (results unlocked). */
export function trackCalcLeadSubmitted(
  calculator: CalculatorKey,
  params: Params = {},
): void {
  trackEvent("calculator_lead_submitted", base(calculator, params));
}

/** Post-submit success state rendered (results now visible to the visitor). */
export function trackCalcLeadSuccess(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_lead_success", base(calculator, params));
}

/** Lead form returned an error. */
export function trackCalcLeadError(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_lead_error", base(calculator, params));
}

/** A property state was chosen in the eligibility gate. */
export function trackCalcStateSelected(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_state_selected", base(calculator, params));
}

/** Results withheld because the state is outside the approved lending config. */
export function trackCalcStateBlocked(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_state_blocked", base(calculator, params));
}

/** Key numbers copied from the results summary. */
export function trackCalcSummaryCopied(calculator: CalculatorKey, params: Params = {}): void {
  trackEvent("calculator_summary_copied", base(calculator, params));
}


/** Results link copied / shared. */
export function trackCalcShare(
  calculator: CalculatorKey,
  method: "copy" | "native" | "email",
): void {
  trackEvent("calculator_results_shared", base(calculator, { method }));
}

/** A shared results link was opened. */
export function trackCalcSharedView(calculator: CalculatorKey): void {
  trackEvent("calculator_shared_results_viewed", base(calculator));
}
